import { useMutation, useQuery } from '@apollo/client/react';
import { Box, Button, Center, Separator, Heading, HStack, Popover, SkeletonText, Spinner, Text, VStack, Field, Presence } from '@chakra-ui/react';
import useDebouncedValue from '@restart/hooks/useDebouncedValue';
import { AppLayoutContextValue } from '@src/components/sco/AppLayout/AppLayoutContext';
import { PITCH_BUILDER_INVESTOR_SEARCH_FRAGMENT } from '@src/components/sco/InvestorMatching/InvestorMatchPitchBuilder/index';
import { MarkdownRenderer } from '@src/components/sco/MarkdownRenderer';
import { PaywalledAction } from '@src/components/sco/PaywalledAction';
import { TextareaListField } from '@src/components/sco/TextareaListField';
import { useDelayedText } from '@src/components/sco/TypingText';
import { UserAccessGate } from '@src/components/sco/UserAccessGate';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { useUserAccessCheck } from '@src/hooks/useUserAccessCheck';
import { gql } from '@src/lib/gql';
import React, { useEffect, useState } from 'react';
import { LuArrowRight, LuPencil, LuRefreshCcw } from 'react-icons/lu';
export const PitchBuilderPreview: React.FC<{
  investorMatchAppLayout?: AppLayoutContextValue;
  investorSearchId: string;
}> = props => {
  const {
    accessCheckPassed
  } = useUserAccessCheck({
    require: {
      contentPermissions: ['canAccessInvestorMatching']
    }
  });
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const [isStale, setIsStale] = useState(false);
  const query = useQuery(gql`
      query ($investorSearchId: String!) {
        scoInvestorSearch(investorSearchId: $investorSearchId) {
          ${PITCH_BUILDER_INVESTOR_SEARCH_FRAGMENT}
        }
      }
    `, {
    variables: {
      investorSearchId: props.investorSearchId
    }
  });
  const [upsertInvestorSearch, upsertInvestorSearchMutation] = useMutation(gql`
    mutation ($input: UpsertInvestorSearchInput!) {
      upsertInvestorSearch(input: $input) {
        investorSearch {
          id
          investorSearchId
          ${PITCH_BUILDER_INVESTOR_SEARCH_FRAGMENT}
        }
      }
    }
  `);
  const performSave = () => {
    if (!investorSearch.investorSearchId) {
      alert('Cannot save, record not loaded yet.');
      return;
    }
    return upsertInvestorSearch({
      variables: {
        input: {
          investorSearchId: props.investorSearchId,
          emailTemplatePrompt
        }
      }
    });
  };
  const doRegenerate = () => {
    if (!investorSearch.investorSearchId) {
      alert('Cannot save, record not loaded yet.');
      return;
    }
    return upsertInvestorSearch({
      variables: {
        input: {
          investorSearchId: investorSearch.investorSearchId,
          reprocessEmailTemplate: true
        }
      }
    });
  };
  const investorSearch = query.data?.scoInvestorSearch;
  const showPreview = !!investorSearch?.emailTemplateGeneratedBody;
  const delayedText = useDelayedText(investorSearch?.emailTemplateGeneratedBody, 5, showPreview);
  const checksumValue = [
  // investorSearch?.pitchDeckUrl,
  investorSearch?.companyName, investorSearch?.companyWebsiteUrl, investorSearch?.companyElevatorPitch, investorSearch?.problemStatement, investorSearch?.solutionStatement, investorSearch?.marketSizeStatement, investorSearch?.tractionStatement, investorSearch?.financialsStatement, investorSearch?.teamStatement, investorSearch?.fundingGoalInDollars, investorSearch?.fundingGoalStageKey, investorSearch?.fundingGoalStructureKeys?.join(', '), investorSearch?.emailTemplateSignature, investorSearch?.emailTemplatePrompt].join('; ');
  const debouncedChecksumValue = useDebouncedValue(checksumValue, 1000);
  const debouncedIsStale = useDebouncedValue(isStale, 250);
  const [emailTemplatePrompt, setEmailTemplatePrompt] = useState(investorSearch?.emailTemplatePrompt || '');
  useEffect(() => {
    if (investorSearch && investorSearch.emailTemplatePrompt !== emailTemplatePrompt) {
      setEmailTemplatePrompt(investorSearch.emailTemplatePrompt);
    }
  }, [investorSearch?.emailTemplatePrompt]);
  useEffect(() => {
    if (!showPreview && checksumValue.length > 600) {
      doRegenerate();
    }
  }, [showPreview, checksumValue]);
  useEffect(() => {
    if (isStale && debouncedIsStale && !upsertInvestorSearchMutation.loading) {
      setIsStale(false);
      doRegenerate();
    }
  }, [isStale, debouncedIsStale]);
  useEffect(() => {
    if (checksumValue !== debouncedChecksumValue) {
      setIsStale(true);
    }
  }, [checksumValue !== debouncedChecksumValue]);
  useEffect(() => {
    setIsStale(false);
  }, [investorSearch?.emailTemplateGeneratedBody]);
  if (query.loading && !investorSearch) {
    return <Center padding={4} width={'full'}>
        <Spinner />
      </Center>;
  }
  if (query.error) {
    return <Center padding={4} width={'full'}>
        <Text>Error: ${query.error.toString()}</Text>
      </Center>;
  }
  return <VStack align={'stretch'} gap={0} height={'full'} justify={'stretch'} width={'full'}>
      <HStack align={'center'} bgColor={'colorPalette.bg'} height={'3.5rem'} justify={'space-between'} padding={3}>
        <HStack>
          <Heading size={'sm'}>Email Pitch Preview</Heading>

          <Presence animationDuration={'moderate'} present={isStale || debouncedIsStale || upsertInvestorSearchMutation.loading} animationName={{
          _open: 'slide-from-bottom, fade-in',
          _closed: 'slide-to-bottom, fade-out'
        }}>
            <HStack>
              <Spinner />
              <Text fontSize={'xs'}>Updates saved, we'll re-generate!</Text>
            </HStack>
          </Presence>
        </HStack>

        <Box>
          <HStack justify={'flex-end'} width={'full'}>
            {/*<Button size={'sm'} variant={'outline'} onClick={() => alert('Todo!')}>*/}
            {/*  Send a Test*/}
            {/*</Button>*/}
            <Button asChild size={'sm'} variant={'cta'}>
              <NextLink href={`/investors/matching/${investorSearch.investorSearchId}/messages`}>
                Continue to Sending
                <LuArrowRight />
              </NextLink>
            </Button>
          </HStack>
        </Box>
      </HStack>
      <Box bg={'colorPalette.panel'} flex={1} opacity={upsertInvestorSearchMutation.loading ? '0.5' : 1} overflow={'scroll'}>
        {showPreview ? <VStack align={'center'} justify={'center'} padding={4}>
            <VStack align={'stretch'} bgColor={'colorPalette.container'} border={'1px solid'} borderColor={'colorPalette.border'} borderRadius={'md'} boxShadow={'md'} gap={4} marginBottom={10} maxWidth={'container.md'} padding={getBreakpointValue([4, 6, 8, 10, 12])} width={'full'}>
              <HStack gap={4}>
                <Heading opacity={0.5} size={'md'}>
                  To:
                </Heading>
                <Heading size={'md'}>{'Investor Contact <contact@investor.com>'}</Heading>
              </HStack>
              <Separator />

              <HStack gap={4}>
                <Heading opacity={0.5} size={'md'}>
                  Re:
                </Heading>
                <Heading size={'md'}>{investorSearch.emailTemplateSubject}</Heading>
              </HStack>
              <Separator />

              {accessCheckPassed ? <>
                  <MarkdownRenderer typographyStyles={{
              p: {
                lineHeight: 1.33,
                paddingY: 2
              }
            }}>
                    {delayedText}
                  </MarkdownRenderer>
                  <HStack bgColor={'colorPalette.bg/50'} borderRadius={'md'} marginTop={3} paddingX={4} paddingY={4}>
                    <Heading flex={1} size={'md'}>
                      How does this look?
                    </Heading>
                    <Box>
                      <Popover.Root positioning={{
                  placement: 'top'
                }}>
                        <Popover.Trigger asChild>
                          <Button size={'sm'} variant={'outline'}>
                            <LuPencil />
                            Suggest Edits
                          </Button>
                        </Popover.Trigger>
                        <Popover.Positioner>
                          <Popover.Content bgColor={'colorPalette.container'} boxShadow={'lg'} maxWidth={'500px'} width={'90vw'}>
                            <Popover.Arrow bgColor={'colorPalette.container'} />
                            <Popover.Body maxW={'container.lg'}>
                              <VStack align={'stretch'} gap={2} padding={2} width={'auto'}>
                                <Field.Root>
                                  <Field.Label>Additional Instructions</Field.Label>
                                  <TextareaListField itemLabel={'Instruction'} value={emailTemplatePrompt} textareaProps={{
                              placeholder: 'Modify phrasing, tone, and more',
                              size: 'sm'
                            }} onChange={setEmailTemplatePrompt} />
                                </Field.Root>
                                <HStack justify={'flex-end'}>
                                  <Popover.CloseTrigger />
                                  <PaywalledAction contentPermission={'canAccessInvestorMatching'} intent={'investor-outreach'} upgradeCtaButtonProps={{
                              width: 'auto',
                              size: 'sm'
                            }} upgradeCtaLabel={'Upgrade to Edit'}>
                                    <Button size={'xs'} variant={'cta'} onClick={() => performSave()}>
                                      Update
                                    </Button>
                                  </PaywalledAction>
                                </HStack>
                              </VStack>
                            </Popover.Body>
                          </Popover.Content>
                        </Popover.Positioner>
                      </Popover.Root>
                    </Box>

                    <Button loading={upsertInvestorSearchMutation.loading} size={'sm'} variant={'outline'} onClick={() => {
                doRegenerate();
              }}>
                      <LuRefreshCcw />
                      Regenerate
                    </Button>
                    <Button asChild size={'sm'} variant={'cta'}>
                      <NextLink href={`/investors/matching/${investorSearch.investorSearchId}/messages`}>
                        Start Sending
                        <LuArrowRight />
                      </NextLink>
                    </Button>
                  </HStack>
                </> : <>
                  <Box maxHeight={'300px'} overflow={'hidden'} position={'relative'} _after={{
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bg: {
                _dark: 'linear-gradient(to bottom, transparent 0%,  #262538 100%)',
                _light: 'linear-gradient(to bottom, transparent 0%,  #FFFBF7 100%)'
              },
              pointerEvents: 'none'
            }}>
                    <MarkdownRenderer typographyStyles={{
                p: {
                  lineHeight: 1.33,
                  paddingY: 2
                }
              }}>
                      {delayedText}
                    </MarkdownRenderer>
                  </Box>
                  <UserAccessGate ctaVariant={'inline-card'} require={{
              contentPermissions: ['canAccessInvestorMatching']
            }} ctaProps={{
              title: 'Upgrade to unlock full Pitch Builder.',
              hideLoginLink: true
            }} />
                </>}

              {/*<SimpleGrid columns={getBreakpointValue([1, 1, 3, 3])} gap={2}>*/}
              {/*  <Button*/}
              {/*    colorScheme={'green'}*/}
              {/*    isLoading={upsertInvestorSearchMutation.loading}*/}
              {/**/}
              {/*    onClick={() => {*/}
              {/*      upsertInvestorSearch({*/}
              {/*        variables: {*/}
              {/*          input: {*/}
              {/*            investorSearchId: investorSearch.investorSearchId,*/}
              {/*            emailTemplateGeneratedBody: null,*/}
              {/*            emailTemplateSubject: null,*/}
              {/*          },*/}
              {/*        },*/}
              {/*      })*/}
              {/*    }}*/}
              {/*    size={'sm'}*/}
              {/*  >*/}
              {/*    Reset*/}
              {/*  </Button>*/}
              {/*</SimpleGrid>*/}
            </VStack>
          </VStack> : <Center height={'full'}>
            <VStack bgColor={'blackAlpha.50'} border={'1px solid'} borderColor={'blackAlpha.200'} borderRadius={'xl'} gap={4} marginBottom={20} maxWidth={'container.md'} padding={[4, 4, 10, 20]} textAlign={'center'}>
              <Box>
                <Heading textAlign={'center'}>Live preview pending...</Heading>
                <Text textAlign={'center'}>
                  Once we know a bit more, we'll show you a live preview of your investor pitch.
                </Text>
              </Box>
              <VStack align={'stretch'} bgColor={'purpleLightAlpha.300'} borderRadius={'md'} gap={8} justify={'stretch'} padding={8} width={'full'}>
                <Box>
                  <SkeletonText gap={2} height={'1em'} lineClamp={1} maxWidth={'100px'} css={{
                '--start-color': 'colorPalette.fg/50',
                '--end-color': 'colorPalette.fg/100'
              }} />
                </Box>
                <Box>
                  <SkeletonText gap={2} lineClamp={2} css={{
                '--start-color': 'colorPalette.fg/50',
                '--end-color': 'colorPalette.fg/100'
              }} />
                </Box>
                <Box>
                  <SkeletonText gap={2} lineClamp={2} css={{
                '--start-color': 'colorPalette.fg/50',
                '--end-color': 'colorPalette.fg/100'
              }} />
                </Box>
                <Box>
                  <SkeletonText gap={2} lineClamp={2} css={{
                '--start-color': 'colorPalette.fg/50',
                '--end-color': 'colorPalette.fg/100'
              }} />
                </Box>
                <Box>
                  <SkeletonText gap={2} lineClamp={2} maxWidth={'150px'} css={{
                '--start-color': 'colorPalette.fg/50',
                '--end-color': 'colorPalette.fg/100'
              }} />
                </Box>
              </VStack>
              <Button loading={upsertInvestorSearchMutation.loading} size={'sm'} variant={'outline'} onClick={() => {
            if (!investorSearch.investorSearchId) {
              alert('Cannot save, record not loaded yet.');
              return;
            }
            upsertInvestorSearch({
              variables: {
                input: {
                  investorSearchId: investorSearch.investorSearchId,
                  reprocessEmailTemplate: true
                }
              }
            });
          }}>
                <LuRefreshCcw />
                Start Generating
              </Button>
            </VStack>
          </Center>}
      </Box>
    </VStack>;
};